import { whenWeglotReady } from '@theme/utilities';

/**
 * TrueFit's own widget scan is switched off in snippets/true-fit-script.liquid whenever
 * Weglot is active, because Weglot resolves the language only after that scan would have
 * run and which of the two wins varies per page load. This module is the single trigger:
 * it waits for the language, then hands it to tfFirstScan, which applies it and scans.
 *
 * The widget's copy lives in a cross-origin iframe Weglot cannot reach, so TrueFit's own
 * `locale` is the only channel — and Shopify publishes `en` alone, so Liquid can't supply the language.
 */

/**
 * TrueFit analytics expects `language_COUNTRY`; unlisted pairs fall back to that format.
 * @type {Record<string, Record<string, string>>}
 */
const TRUEFIT_LOCALES = {
  AU: { en: 'en_AU' },
  NZ: { en: 'en_NZ' },
  GB: { en: 'en_GB' },
  CA: { en: 'en_CA', fr: 'fr_CA' },
  US: { en: 'en_US' },
};

/** Primary subtag — Weglot reports `fr-ca`, the table keys on `fr`. */
const toLang = (code) => (code ?? '').split('-')[0].toLowerCase();

const toTrueFitLocale = (/** @type {string} */ lang, /** @type {string} */ countryCode) => {
  if (!lang) return '';
  if (!countryCode) return lang;

  return TRUEFIT_LOCALES[countryCode]?.[lang] ?? `${lang}_${countryCode}`;
};

const run = async () => {
  let lang = '';

  // Weglot is third-party: whenWeglotReady calls getCurrentLang synchronously, so a throw in
  // there would reject this and leave the widget unscanned until the head deadline.
  try {
    await whenWeglotReady();
    lang = toLang(window.Weglot?.getCurrentLang());
  } catch (error) {
    console.error('TrueFit locale: reading the Weglot language failed', error);
  }

  const countryCode =
    typeof Theme !== 'undefined' ? (Theme.localization?.countryCode ?? '').toUpperCase() : '';

  // Empty means Weglot never resolved; TrueFit then falls back to the rendered data-locale.
  window.tfFirstScan?.(toTrueFitLocale(lang, countryCode));
};

void run();
